import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { authenticationService } from 'src/services/authentication.service';
import { deleteEntry, get } from 'src/services';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import AddBillingTaxType from './AddBillingTaxType';
import EditBillingTaxType from './EditBillingTaxType';

interface BillingTaxTypeData {
  id: number;
  name: string;
  exclusive: boolean;
  createdOn: string;
  status: string;
}

const BillingTaxType = () => {
  const { showSnackbar } = useSnackbar();
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [tableData, setTableData] = useState<BillingTaxTypeData[]>([]);
  const [editedItem, setEditedItem] = useState<BillingTaxTypeData | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<number | null>(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [exclusiveFilter, setExclusiveFilter] = useState('');

  const [nameOptions, setNameOptions] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/billing-tax-type/vendor/${vendorId}/getAll`);
      if (response && response.success && response.data) {
        setTableData(response.data);
      } else if (Array.isArray(response)) {
        setTableData(response);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error('Error fetching billing tax type data:', error);
      showSnackbar('Error fetching data', 'error');
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [namesRes, statusesRes] = await Promise.all([
        get(`${process.env.REACT_APP_BASE_API_URL}/api/billing-tax-type/vendor/${vendorId}/distinct-names`),
        get(`${process.env.REACT_APP_BASE_API_URL}/api/billing-tax-type/vendor/${vendorId}/distinct-statuses`)
      ]);
      
      setNameOptions(namesRes?.data || namesRes || []);
      setStatusOptions(statusesRes?.data || statusesRes || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchFilterOptions();
  }, []);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const getFilteredRows = () => {
    let filteredRows = tableData;

    if (searchQuery) {
      filteredRows = filteredRows.filter((row) =>
        Object.values(row).some(
          (value) => value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (nameFilter) {
      filteredRows = filteredRows.filter(
        (row) => row.name?.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (statusFilter) {
      filteredRows = filteredRows.filter(
        (row) => row.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (exclusiveFilter !== '') {
      filteredRows = filteredRows.filter(
        (row) => row.exclusive === (exclusiveFilter === 'true')
      );
    }

    return filteredRows;
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleNameFilterChange = (e: any) => {
    setNameFilter(e.target.value);
  };

  const handleStatusFilterChange = (e: any) => {
    setStatusFilter(e.target.value);
  };

  const handleExclusiveFilterChange = (e: any) => {
    setExclusiveFilter(e.target.value);
  };

  const handleCloseDialogs = () => {
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setEditedItem(null);
  };

  const editItem = (item: BillingTaxTypeData) => {
    setEditedItem(item);
    setOpenEditDialog(true);
  };

  const deleteItem = (id: number) => {
    setDeleteConfirmationId(id);
    setOpenDeleteConfirmation(true);
  };

  const performDelete = async (id: number) => {
    try {
      const response = await deleteEntry(`${process.env.REACT_APP_BASE_API_URL}/api/billing-tax-type/delete/${id}`);
      if (response && (response.success || response === 'Successfully deleted')) {
        showSnackbar('Billing tax type deleted successfully.', 'success');
        await fetchData();
      } else {
        showSnackbar('Error deleting item.', 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      showSnackbar('Error deleting item.', 'error');
    } finally {
      setDeleteConfirmationId(null);
      setOpenDeleteConfirmation(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { 
      field: 'exclusive', 
      headerName: 'Exclusive', 
      flex: 1,
      renderCell: (params) => params.value ? 'Yes' : 'No'
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
      valueGetter: (params) => params.value ? dayjs(params.value).format('DD/MMM/YYYY') : '',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => (
        <span style={{ 
          color: params.value === 'Approved' ? 'green' : params.value === 'Pending' ? 'orange' : 'red',
          fontWeight: 'bold'
        }}>
          {params.value}
        </span>
      )
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <>
          {hasPermission(["OWNER", "billing_tax_type.edit"]) && (
            <EditIcon
              style={{ cursor: 'pointer', marginRight: '8px', color: '#1976D2' }}
              onClick={() => editItem(params.row)}
            />
          )}
          {hasPermission(["OWNER", "billing_tax_type.delete"]) && (
            <DeleteIcon
              style={{ cursor: 'pointer', color: '#E53935' }}
              onClick={() => deleteItem(params.row.id)}
            />
          )}
        </>
      )
    }
  ];

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  return (
    <>
      <Helmet>
        <title>Manage Billing Tax Type</title>
      </Helmet>

      {/* Add Dialog */}
      {hasPermission(["OWNER", "billing_tax_type.create"]) && (
        <Dialog
          open={openAddDialog}
          onClose={handleCloseDialogs}
          aria-labelledby="add-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="add-dialog-title">Add Billing Tax Type</DialogTitle>
          <DialogContent>
            {openAddDialog && (
              <AddBillingTaxType
                handleClose={handleCloseDialogs}
                fetchData={fetchData}
                showSnackbar={showSnackbar}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {hasPermission(["OWNER", "billing_tax_type.edit"]) && (
        <Dialog
          open={openEditDialog}
          onClose={handleCloseDialogs}
          aria-labelledby="edit-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="edit-dialog-title">Edit Billing Tax Type</DialogTitle>
          <DialogContent>
            {editedItem && (
              <EditBillingTaxType
                itemData={editedItem}
                handleClose={handleCloseDialogs}
                handleRefresh={fetchData}
                showSnackbar={showSnackbar}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <PageTitleWrapper>
        {hasPermission(["OWNER", "billing_tax_type.create"]) ? (
          <PageTitle
            heading="Billing Tax Type List"
            subHeading="Manage billing tax types and configurations."
            label="Add Billing Tax Type"
            handleClick={handleOpenAddDialog}
          />
        ) : (
          <PageTitle
            heading="Billing Tax Type List"
            subHeading="Manage billing tax types and configurations."
            handleClick={() => {}}
          />
        )}
      </PageTitleWrapper>

      {/* Filters Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, width: '100%', padding: '0 15px', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearch}
          size="small"
          sx={{ minWidth: '200px', flex: 1 }}
        />

        <FormControl variant="outlined" size="small" sx={{ minWidth: '180px', flex: 1 }}>
          <InputLabel>Name Filter</InputLabel>
          <Select
            value={nameFilter}
            onChange={handleNameFilterChange}
            label="Name Filter"
          >
            <MenuItem value="">ALL</MenuItem>
            {nameOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: '180px', flex: 1 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status Filter"
          >
            <MenuItem value="">ALL</MenuItem>
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: '180px', flex: 1 }}>
          <InputLabel>Exclusive Filter</InputLabel>
          <Select
            value={exclusiveFilter}
            onChange={handleExclusiveFilterChange}
            label="Exclusive Filter"
          >
            <MenuItem value="">ALL</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={getFilteredRows()}
          columns={columns}
          loading={loading}
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
            },
            pagination: {
              labelRowsPerPage: 'Rows per page:',
            },
          }}
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell:hover': {
              color: 'rgb(43 44 45)',
            },
          }}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          autoHeight={false}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this billing tax type?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirmation(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (deleteConfirmationId) {
                performDelete(deleteConfirmationId);
              }
            }}
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BillingTaxType;